/**
 * Named iframe sandbox token sets.
 *
 * Never combine allow-scripts with allow-same-origin on a same-origin or
 * srcdoc frame — the framed document can then remove its own sandbox.
 */

/** Cross-origin video players (YouTube, Tella, Loom, Vimeo). */
export const VIDEO_EMBED_SANDBOX =
  "allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox";

/**
 * clipboard-write is required for YouTube share/copy-link in the player.
 * The document Permissions-Policy must also list the player origin — iframe
 * allow cannot re-enable a feature the header omits.
 */
export const VIDEO_EMBED_ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture";

/**
 * Hosts that may receive VIDEO_EMBED_SANDBOX (scripts + same-origin).
 * Matches the video origins already listed in CSP frame-src.
 */
const VIDEO_EMBED_HOST_SUFFIXES = [
  "youtube.com",
  "youtube-nocookie.com",
  "tella.tv",
  "loom.com",
  "vimeo.com",
] as const;

/** True only for https player origins; never for same-origin app URLs. */
export function isAllowedVideoEmbedUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return false;
    const host = parsed.hostname.toLowerCase();
    return VIDEO_EMBED_HOST_SUFFIXES.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`)
    );
  } catch {
    return false;
  }
}

/** GTM noscript tracking pixel. Empty sandbox is fully locked down. */
export const GTM_NOSCRIPT_SANDBOX = "";

/**
 * Print srcdoc iframe. allow-modals is required for window.print().
 * allow-same-origin lets the parent call contentWindow.print().
 * Do not add allow-scripts.
 */
export const PRINT_SRCDOC_SANDBOX = "allow-modals allow-same-origin";

/**
 * Hidden srcdoc iframe for print. Sandbox is applied before srcdoc so the
 * document never loads same-origin unsandboxed.
 */
export function createPrintSrcDocIframe(
  html: string,
  doc: Document = document
): HTMLIFrameElement {
  const iframe = doc.createElement("iframe");
  iframe.setAttribute("sandbox", PRINT_SRCDOC_SANDBOX);
  iframe.setAttribute("referrerpolicy", "no-referrer");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  doc.body.appendChild(iframe);
  iframe.srcdoc = html;
  return iframe;
}
